/**
 * How a flat `<select>` option list becomes `<optgroup>`s — U3.4.
 *
 * ⚠ ITS OWN FILE ON THE LINTER'S OWN ADVICE. It started life inside `panel.tsx`, which
 * exports components, and `react-refresh/only-export-components` flagged it immediately:
 * "use a new file to share constants or functions between components". The rest of that
 * warning class in this repo is pre-existing and left alone; adding a new one to it would
 * be the way a lint baseline stops meaning anything.
 *
 * ⚠ MOVED FROM `features/inquiries/` TO `components/ui/` IN U3.3, alongside the component
 * it serves. It never had anything to do with inquiries — it lived there only because the
 * select did, which is the whole finding OQ-SELECTFIELD-UI records.
 */

/**
 * One option. `group` is OPTIONAL and additive.
 *
 * ⚠ AN OPTION SET WITH NO `group` RENDERS EXACTLY AS IT ALWAYS DID: a flat list of
 * `<option>`s, no `<optgroup>`, no "Ungrouped". Every existing caller of `SelectField`
 * passes options without it and must be untouched by this change, which is the same
 * "a country with no groups sees no change at all" rule the quote PDF follows.
 */
export interface SelectFieldOption {
  value: string;
  label: string;
  /** The `<optgroup>` heading this option sits under, if the caller groups at all. */
  group?: string;
}

/**
 * Consecutive options that share a `group`, in the caller's order.
 *
 * RUNS, not buckets — the same choice the quote PDF makes. Gathering every "Addon" option
 * together would reorder a list the server ordered deliberately, and the picker would then
 * disagree with the printed document it is used to build.
 *
 * That puts the ordering burden where it belongs, on whoever supplies the options:
 * `compareCatalogue` in `features/quotes/use-quotes.ts` is what sorts the picker so its runs
 * come out one per shelf. This function does not sort, ever.
 */
export function optionRuns(
  options: readonly SelectFieldOption[],
): { group: string | undefined; options: SelectFieldOption[] }[] {
  const runs: { group: string | undefined; options: SelectFieldOption[] }[] = [];
  for (const option of options) {
    const last = runs[runs.length - 1];
    if (last !== undefined && last.group === option.group) {
      last.options.push(option);
    } else {
      runs.push({ group: option.group, options: [option] });
    }
  }
  return runs;
}
